import { cache } from 'swr';
import request from 'services/request';
import { endpoints } from 'services/endpoints';

export const EXAONE_ACCESS_TTL_MS = 5 * 60 * 1000;
export const EXAONE_ACCESS_ERROR_TTL_MS = 60 * 1000;
export const EXAONE_ACCESS_REQUEST_TIMEOUT_MS = 5000;
const ACCESS_CACHE_KEY = 'exaone:access';

export interface ExaOneAccessSnapshot {
  userId: string;
  available: boolean;
  /** 서버가 명시적으로 이용 불가를 응답한 경우에만 설정한다. */
  denied?: boolean;
  expiresAt: number;
}
interface AccessEntry {
  userId: string;
  refreshToken: string;
  snapshot?: ExaOneAccessSnapshot;
  pending?: Promise<ExaOneAccessSnapshot | undefined>;
}

// JWT는 로그인 사용자 식별에만 사용하며 권한 claim은 읽지 않는다.
export const readAuthUserId = (token: string | null = localStorage.getItem('llAccessToken')): string => {
  try {
    const payload = (token || '').split('.')[1];
    const data = JSON.parse(decodeURIComponent(escape(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))));
    return typeof data?.userId === 'string' ? data.userId : '';
  } catch { return ''; }
};
const isCurrent = (entry: AccessEntry) => entry.userId === readAuthUserId()
  && entry.refreshToken === localStorage.getItem('llRefreshToken');
const currentEntry = (userId: string): AccessEntry | undefined => {
  const entry: AccessEntry | undefined = cache.get(ACCESS_CACHE_KEY);
  return userId && entry?.userId === userId && isCurrent(entry) ? entry : undefined;
};
export const clearExaOneAccessCache = () => cache.delete(ACCESS_CACHE_KEY);
export const subscribeExaOneAccess = (listener: () => void) => {
  let previous = cache.get(ACCESS_CACHE_KEY);
  return cache.subscribe(() => {
    const next = cache.get(ACCESS_CACHE_KEY);
    if (next !== previous) { previous = next; listener(); }
  });
};
export const getCachedExaOneAccess = (userId: string, includeExpired = false) => {
  const snapshot = currentEntry(userId)?.snapshot;
  return snapshot && (includeExpired || snapshot.expiresAt > Date.now()) ? snapshot : undefined;
};

export const loadExaOneAccess = (userId: string): Promise<ExaOneAccessSnapshot | undefined> => {
  const refreshToken = localStorage.getItem('llRefreshToken');
  if (!userId || userId !== readAuthUserId() || !refreshToken) return Promise.resolve(undefined);
  const previous = currentEntry(userId);
  const hit = getCachedExaOneAccess(userId);
  if (hit) return Promise.resolve(hit);
  if (previous?.pending) return previous.pending;

  const entry: AccessEntry = { userId, refreshToken, snapshot: previous?.snapshot };
  const store = (available: boolean, ttl: number, denied = false) => {
    // 기존 cache.clear(), 계정 변경, 채팅 권한 거부 이후의 늦은 응답은 저장하지 않는다.
    if (cache.get(ACCESS_CACHE_KEY) !== entry || !isCurrent(entry)) return undefined;
    const snapshot = { userId, available, denied, expiresAt: Date.now() + ttl };
    cache.set(ACCESS_CACHE_KEY, { userId, refreshToken, snapshot });
    return snapshot;
  };
  // 일시적인 조회 실패는 권한 회수가 아니다. 같은 로그인에서 확인한 표시 상태를 유지한다.
  // 실제 채팅 권한은 서버가 매 요청마다 다시 검사하며, 조회는 오류 TTL 후 재시도한다.
  const storeTemporaryFailure = () => store(entry.snapshot?.available === true, EXAONE_ACCESS_ERROR_TTL_MS);
  let timeout: ReturnType<typeof setTimeout> | undefined;
  entry.pending = Promise.race([
    request({ url: endpoints.exaoneChatbot.access, method: 'get', timeout: EXAONE_ACCESS_REQUEST_TIMEOUT_MS }, { skipGatewayRedirect: true }),
    new Promise<never>((_, reject) => {
      timeout = setTimeout(() => reject(new Error('EXAONE access lookup timed out')), EXAONE_ACCESS_REQUEST_TIMEOUT_MS);
    }),
  ]).then(data => {
    const valid = data?.resultCode === 200 && typeof data.available === 'boolean';
    if (valid) return store(data.available, EXAONE_ACCESS_TTL_MS, !data.available);
    if (isExaOneAccessDenied(data?.resultCode)) return store(false, EXAONE_ACCESS_TTL_MS, true);
    return storeTemporaryFailure();
  }).catch(storeTemporaryFailure)
    .finally(() => { if (timeout !== undefined) clearTimeout(timeout); });
  cache.set(ACCESS_CACHE_KEY, entry);
  return entry.pending;
};

export const isExaOneAccessDenied = (code: number) => code === 403 || code === 1000 || code === 1029;
export const revokeExaOneAccess = (
  accessToken = localStorage.getItem('llAccessToken'),
  refreshToken = localStorage.getItem('llRefreshToken'),
  ttl = EXAONE_ACCESS_TTL_MS,
  denied = true
) => {
  const userId = readAuthUserId(accessToken);
  if (!userId || !refreshToken || refreshToken !== localStorage.getItem('llRefreshToken') || userId !== readAuthUserId()) return;
  cache.set(ACCESS_CACHE_KEY, { userId, refreshToken, snapshot: { userId, available: false, denied, expiresAt: Date.now() + ttl } });
};
